import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './use-toast';

interface UseCopyToClipboardReturn {
  copyToClipboard: (text: string) => Promise<boolean>;
  markCopied: () => void;
  isCopied: boolean;
  error: string | null;
}

interface UseCopyToClipboardProps {
  /** 复制成功后显示的提示持续时间，单位毫秒 */
  duration?: number;
}

/**
 * 复制文本到剪贴板的React钩子
 * @returns {UseCopyToClipboardReturn} 包含复制函数和状态的钩子
 * 
 * @example
 * const { copyToClipboard, isCopied, error } = useCopyToClipboard();
 * await copyToClipboard('要复制的文本');
 */
export function useCopyToClipboard({ duration = 3000 }: UseCopyToClipboardProps = {}): UseCopyToClipboardReturn {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const markCopied = useCallback(() => {
    setIsCopied(true);
    setError(null);
    setTimeout(() => {
      setIsCopied(false);
    }, duration);
  }, [duration]);

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    // 重置状态
    setIsCopied(false);
    setError(null);

    // 检查浏览器是否支持Clipboard API
    if (!navigator.clipboard) {
      setError('浏览器不支持剪贴板API');
      return false;
    }

    // 检查文本是否为空
    if (!text || text.trim() === '') {
      setError('复制的文本不能为空');
      return false;
    }

    try {
      // 使用现代Clipboard API
      await navigator.clipboard.writeText(text);
      markCopied();
      toast.default({
        description: t('copy_clipboard.success'),
      });

      return true;
    } catch (err) {
      // 如果Clipboard API失败，尝试使用传统的execCommand方法作为降级方案
      try {
        // 创建临时textarea元素
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);

        // 选中文本
        textArea.focus();
        textArea.select();

        // 执行复制命令
        const successful = document.execCommand('copy');

        // 清理临时元素
        document.body.removeChild(textArea);

        if (successful) {
          markCopied();
          return true;
        } else {
          setError('复制操作失败');
          return false;
        }
      } catch (fallbackErr) {
        setError('复制失败，请检查浏览器权限设置');
        return false;
      }
    }
  }, [duration, markCopied, t, toast]);

  return {
    copyToClipboard,
    markCopied,
    isCopied,
    error,
  };
}

export default useCopyToClipboard;